import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { IEventCore, IRound } from '../../models/IEvent';
import { Typography, Container, Grid, Theme, Fab, Box, Tooltip, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import AuthContext from '../../services/Auth/AuthContext';

import { useNavigate, useParams } from 'react-router-dom';
import LiveEvent from './LiveEvent';
import RidersTable from './RidersTable';
import IRider from '../../models/IRider';
import { SnackbarProvider, useSnackbar } from 'notistack';
import EventServiceInstance from '../../services/EventService';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import EventHeader from './EventHeader';
import { useTranslation } from 'react-i18next';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            paddingTop: theme.spacing(10),
            paddingBottom: theme.spacing(4),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
        fab: {
            position: 'fixed',
            bottom: theme.spacing(2),
            right: theme.spacing(2),
        },
        fabUnmask: {
            position: 'fixed',
            bottom: theme.spacing(2),
            right: theme.spacing(10),
        },
    })
);

interface IEventToolbarProps {
    eventId: string;
}
function EventToolbar(props: IEventToolbarProps) {
    const navigate = useNavigate();
    return (
        <Fab
            onClick={() => navigate(`/event/setup/${props.eventId}`)}
            color="primary"
            aria-label="edit"
            className={useStyles().fab}
        >
            <EditIcon />
        </Fab>
    );
}

interface IUnmaskButtonProps {
    unmasked: boolean;
    onToggle: () => void;
}
function UnmaskButton(props: IUnmaskButtonProps) {
    const classes = useStyles();
    const { t } = useTranslation('common');
    return (
        <Tooltip title={props.unmasked ? t('events.maskResults') : t('events.unmaskResults')}>
            <Fab
                onClick={props.onToggle}
                color={props.unmasked ? 'secondary' : 'default'}
                aria-label={props.unmasked ? 'mask results' : 'unmask results'}
                className={classes.fabUnmask}
            >
                {props.unmasked ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </Fab>
        </Tooltip>
    );
}

function EventDetail() {
    const { eventId } = useParams();
    const [eventData, setEventData] = useState<IEventCore | null>();
    const [riders, setRiders] = useState<IRider[]>([]);
    const [unmasked, setUnmasked] = useState(false);
    const [rounds, setRounds] = useState<IRound[]>([]);
    const [selectedRound, setSelectedRound] = useState<string>('');
    const authService = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation('common');

    useEffect(() => {
        const source = axios.CancelToken.source();
        const fetchData = async () => {
            if (!eventId) {
                throw new Error("EventId is not defined");
            }
            const evt = await EventServiceInstance.getEvent(eventId, source.token);
            if (evt?.status === 1 || evt?.status === 2) {
                const [ridersData, roundsData] = await Promise.all([
                    EventServiceInstance.getRiders(eventId, unmasked, source.token),
                    EventServiceInstance.getRounds(eventId, source.token),
                ]);
                setEventData(evt);
                setRiders(ridersData);
                setRounds(roundsData);
            } else {
                setEventData(evt);
            }
        };
        fetchData().catch((error) => {
            if (!axios.isCancel(error)) {
                console.error(error);
            }
        });
        return () => {
            source.cancel();
        };
    }, [eventId, unmasked]);

    const fetchRidersByRound = async (roundId: string) => {
        if (!eventId) return;
        if (roundId) {
            const roundRiders = await EventServiceInstance.getRidersByRound(eventId, roundId);
            setRiders(roundRiders);
        } else {
            const allRiders = await EventServiceInstance.getRiders(eventId, unmasked);
            setRiders(allRiders);
        }
    };

    const handleRoundChange = async (roundId: string) => {
        setSelectedRound(roundId);
        await fetchRidersByRound(roundId);
    };

    const getActiveRound = (): IRound | undefined => {
        return rounds.find(r => !r.end);
    };

    const handleStartRound = async () => {
        if (!eventId) return;
        try {
            const newRound = await EventServiceInstance.startRound(eventId);
            setRounds(prev => [...prev, newRound]);
        } catch (e) {
            enqueueSnackbar(t('rounds.startRoundError'), { variant: 'error' });
        }
    };

    const handleStopRound = async () => {
        if (!eventId) return;
        try {
            const updatedRound = await EventServiceInstance.endRound(eventId);
            setRounds(prev => prev.map(r => r.id === updatedRound.id ? updatedRound : r));
        } catch (e) {
            enqueueSnackbar(t('rounds.stopRoundError'), { variant: 'error' });
        }
    };

    const classes = useStyles();

    if (!eventId) {
        throw new Error("EventId is not defined");
    }

    const isOrganizer = authService?.getIsOrganizer(eventData?.organizerId ?? '');
    const eventsNameMap: Record<string, string> = { [t('mainList.events')]: '/events' };
    const allowRounds = eventData?.allowRounds === true;
    const hasRounds = rounds.length > 0;
    const activeRound = getActiveRound();

    const roundFilter = hasRounds ? (
        <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="round-filter-label">{t('rounds.filterByRound')}</InputLabel>
            <Select
                labelId="round-filter-label"
                id="round-filter"
                value={selectedRound}
                label={t('rounds.filterByRound')}
                onChange={(e) => handleRoundChange(e.target.value as string)}
            >
                <MenuItem value="">{t('rounds.allRounds')}</MenuItem>
                {rounds.map(r => (
                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
            </Select>
        </FormControl>
    ) : null;

    return (
        <Container maxWidth={false}>
            <Box className={classes.container}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={eventsNameMap} current={eventData?.name ?? ''} />
                    </Grid>
                </Grid>
                {eventData?.status === 1 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <SnackbarProvider maxSnack={3}>
                                {isOrganizer && <EventToolbar eventId={eventId} />}
                                {isOrganizer && eventData?.raceMode === false && (
                                    <UnmaskButton
                                        unmasked={unmasked}
                                        onToggle={() => setUnmasked(prev => !prev)}
                                    />
                                )}
                                <EventHeader
                                    title={eventData?.name}
                                    date={eventData?.eventDate}
                                    organizerName={eventData?.organizerName}
                                />
                                {isOrganizer && allowRounds && (
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        {activeRound ? (
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                startIcon={<StopIcon />}
                                                onClick={handleStopRound}
                                            >
                                                {t('rounds.stopRound')}
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={<PlayArrowIcon />}
                                                onClick={handleStartRound}
                                            >
                                                {t('rounds.startRound')}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                                {allowRounds && hasRounds && <Box sx={{ mb: 1 }}>{roundFilter}</Box>}
                                {selectedRound ? (
                                    <RidersTable
                                        eventId={eventId}
                                        riders={riders}
                                        isOrganizer={isOrganizer}
                                        groups={eventData?.groups}
                                        selectedRound={selectedRound}
                                    />
                                ) : (
                                    <LiveEvent
                                        eventId={eventId}
                                        isOrganizer={isOrganizer}
                                        groups={eventData?.groups}
                                        unmasked={unmasked}
                                        riders={riders}
                                        selectedRound={selectedRound}
                                    />
                                )}
                            </SnackbarProvider>
                        </Grid>
                    </Grid>
                )}
                {eventData?.status === 2 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {isOrganizer && <EventToolbar eventId={eventId} />}
                            {isOrganizer && eventData?.raceMode === false && (
                                <UnmaskButton
                                    unmasked={unmasked}
                                    onToggle={() => setUnmasked(prev => !prev)}
                                />
                            )}
                            <EventHeader
                                title={eventData?.name}
                                date={eventData?.eventDate}
                                organizerName={eventData?.organizerName}
                            />
                            {allowRounds && hasRounds && <Box sx={{ mb: 1 }}>{roundFilter}</Box>}
                            <RidersTable eventId={eventId} riders={riders} isOrganizer={isOrganizer} groups={eventData?.groups} selectedRound={selectedRound} />
                        </Grid>
                    </Grid>
                )}
                {eventData?.status === 0 && (
                    <Typography variant="body1" align="center">
                        Event has not started yet.
                    </Typography>
                )}
            </Box>
        </Container>
    );
}

export default EventDetail;